// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {KNOCKOUT_DOWN_MS, RoomKnockout, type SlapOutcome} from '@app/features/voice_scene/net/RoomKnockout';
import {EMOTE_SEND_LIMIT, RoomSignalLimiter} from '@app/features/voice_scene/net/RoomSignals';
import type {RoomEmote} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import {makeAutoObservable} from 'mobx';

// at: when the movement began (performance.now()), for a slap that lands after it was seen; now without it.
export type RoomAvatarSignal =
	| {identity: string; emote: RoomEmote; at?: number}
	| {identity: string; head: {yawDeg: number; pitchDeg: number}};

const LOCAL_SENDER = 'local';

// Emotes and head directions that passed the room's filters, for the characters to play; and whose reactions this
// viewer hides. Only this client, only this session.
class RoomAvatarSignalsState {
	private hidden: ReadonlySet<string> = new Set();
	// My own emotes are held to the sender limit, which is stricter than the receivers' (they allow for network jitter),
	// so what I send is not dropped by them.
	emoteSendLimited = false;
	// My character lies knocked out (RoomKnockout): it neither walks nor moves until this clears.
	knockedOut = false;
	private readonly knockout = new RoomKnockout();
	private knockoutTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly listeners = new Set<(signal: RoomAvatarSignal) => void>();
	private readonly sendLimiter = new RoomSignalLimiter(EMOTE_SEND_LIMIT);
	private sendTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<
			RoomAvatarSignalsState,
			'listeners' | 'sendLimiter' | 'sendTimer' | 'knockout' | 'knockoutTimer'
		>(
			this,
			{listeners: false, sendLimiter: false, sendTimer: false, knockout: false, knockoutTimer: false},
			{autoBind: true},
		);
		ResettableStates.register(this, this.reset);
	}

	subscribe(cb: (signal: RoomAvatarSignal) => void): () => void {
		this.listeners.add(cb);
		return () => {
			this.listeners.delete(cb);
		};
	}

	emit(signal: RoomAvatarSignal): void {
		for (const cb of [...this.listeners]) cb(signal);
	}

	hide(userId: string): void {
		this.hidden = new Set(this.hidden).add(userId);
	}

	unhide(userId: string): void {
		const next = new Set(this.hidden);
		next.delete(userId);
		this.hidden = next;
	}

	isHidden(userId: string): boolean {
		return this.hidden.has(userId);
	}

	// Records one emote of mine; false while the limit holds. One timer clears emoteSendLimited when it ends.
	takeEmoteSend(now: number): boolean {
		if (!this.sendLimiter.allowEmote(LOCAL_SENDER, now)) return false;
		const wait = this.sendLimiter.nextEmoteAt(LOCAL_SENDER, now) - now;
		this.emoteSendLimited = wait > 0;
		if (this.sendTimer !== null) clearTimeout(this.sendTimer);
		this.sendTimer = wait > 0 ? setTimeout(this.endSendLimit, wait) : null;
		return true;
	}

	// How a slap at me would land now; null while I lie knocked out. landSlap once its answer went out.
	slapOutcome(now: number): SlapOutcome | null {
		return this.knockout.outcome(now);
	}

	landSlap(outcome: SlapOutcome, now: number): void {
		this.knockout.land(outcome, now);
		if (outcome !== 'knockout') return;
		this.knockedOut = true;
		if (this.knockoutTimer !== null) clearTimeout(this.knockoutTimer);
		this.knockoutTimer = setTimeout(this.endKnockout, KNOCKOUT_DOWN_MS);
	}

	private endKnockout(): void {
		this.knockoutTimer = null;
		this.knockedOut = false;
	}

	private endSendLimit(): void {
		this.sendTimer = null;
		this.emoteSendLimited = false;
	}

	reset(): void {
		this.hidden = new Set();
		this.sendLimiter.clear();
		if (this.sendTimer !== null) clearTimeout(this.sendTimer);
		this.sendTimer = null;
		this.emoteSendLimited = false;
		this.knockout.clear();
		if (this.knockoutTimer !== null) clearTimeout(this.knockoutTimer);
		this.knockoutTimer = null;
		this.knockedOut = false;
	}
}

const RoomAvatarSignals = new RoomAvatarSignalsState();
export default RoomAvatarSignals;
